import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import {
    Plus,
    Edit,
    Trash2,
    Save,
    X,
    Book,
    Clock,
    Video,
    FileText,
    Eye,
    PlusCircle,
    MinusCircle
} from 'lucide-react';

/**
 * ExperimentManagement.jsx
 *
 * CRUD UI for experiments (matches style & behavior of LessonManagement.jsx)
 * - Option A: Full question builder (MCQ / TrueFalse / Text)
 * - Do NOT send `questions` in payload when empty (as requested)
 *
 * Notes:
 * - adminService must expose:
 *   createExperiment(data) -> POST /admin/experiments
 *   getExperiments(params) -> GET /admin/experiments?lesson_id=...
 *   updateExperiment(id, data) -> PUT /admin/experiments/:id
 *   deleteExperiment(id) -> DELETE /admin/experiments/:id
 * - Re-uses admin endpoints used by LessonManagement for fetching levels/terms/lessons
 */

const QUESTION_TYPES = [
    { value: 'mcq', label: 'Multiple Choice' },
    { value: 'true_false', label: 'True / False' },
    { value: 'text', label: 'Text Answer' }
];

const ExperimentManagement = () => {
    const [experiments, setExperiments] = useState([]);
    const [levels, setLevels] = useState([]);
    const [terms, setTerms] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(false);

    // filters
    const [selectedLevel, setSelectedLevel] = useState('all');
    const [selectedTerm, setSelectedTerm] = useState('all');
    const [selectedLesson, setSelectedLesson] = useState('all');

    // modal / form
    const [showModal, setShowModal] = useState(false);
    const [editingExperiment, setEditingExperiment] = useState(null);
    const [formData, setFormData] = useState({
        lesson_id: '',
        title: '',
        description: '',
        instructions: '',
        materials_needed: '',
        passing_score: 70,
        max_attempts: 3,
        experiment_type: 'mcq',
        experiment_data: {},
        // questions will be managed separately
    });

    const [questions, setQuestions] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        // whenever filters change, refetch experiments
        fetchExperiments();
        // if level changes, load terms & reset filters
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLevel, selectedTerm, selectedLesson]);

    // Initial load: levels, lessons (all), experiments
    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [levelsRes, lessonsRes, experimentsRes] = await Promise.all([
                adminService.getLevels(), // expected { levels: [...] }
                adminService.getLessons(), // expected { lessons: [...] }
                adminService.getExperiments() // expected { experiments: [...] }
            ]);

            setLevels(levelsRes.levels || []);
            setLessons(lessonsRes.lessons || []);
            setExperiments(experimentsRes.experiments || []);
        } catch (err) {
            console.error('Failed to fetch initial data:', err);
            alert('Failed to fetch initial data: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    const fetchTermsByLevel = async (levelId) => {
        try {
            const response = await adminService.getTerms({ level_id: levelId });
            setTerms(response.terms || []);
        } catch (err) {
            console.error('Failed to fetch terms:', err);
            setTerms([]);
        }
    };

    const fetchLessonsByTermOrLevel = async (params = {}) => {
        try {
            const res = await adminService.getLessons(params); // supports { term_id, level_id }
            setLessons(res.lessons || []);
        } catch (err) {
            console.error('Failed to fetch lessons:', err);
            setLessons([]);
        }
    };

    const fetchExperiments = async () => {
        try {
            setLoading(true);
            const params = {};
            if (selectedLesson !== 'all') {
                params.lesson_id = selectedLesson;
            } else if (selectedTerm !== 'all') {
                // If API supports filtering by term via lessons, we'd need lesson IDs; to keep simple,
                // we'll fetch experiments without lesson filter if lesson not selected.
            }
            const res = await adminService.getExperiments(params);
            setExperiments(res.experiments || []);
        } catch (err) {
            console.error('Failed to fetch experiments:', err);
            alert('Failed to fetch experiments: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    // Open modal for create or edit
    const handleOpenModal = async (experiment = null) => {
        setErrors({});
        if (experiment) {
            setEditingExperiment(experiment);
            setFormData({
                lesson_id: experiment.lesson_id || '',
                title: experiment.title || '',
                description: experiment.description || '',
                instructions: experiment.instructions || '',
                materials_needed: experiment.materials_needed || '',
                passing_score: experiment.passing_score ?? 70,
                max_attempts: experiment.max_attempts ?? 3,
                experiment_type: experiment.experiment_type || 'mcq',
                experiment_data: experiment.experiment_data ? JSON.parseSafe?.(experiment.experiment_data) ?? experiment.experiment_data : {}
            });

            // attempt to fetch questions for this experiment if API returns them via a separate endpoint
            // but per provided API, GET /admin/experiments returns experiments without questions.
            // If you have an endpoint to fetch questions, integrate it here.
            // For now, if experiment has `questions` field (some backends may include), use it:
            setQuestions(experiment.questions ? (Array.isArray(experiment.questions) ? experiment.questions.map(normalizeQuestion) : []) : []);
        } else {
            setEditingExperiment(null);
            setFormData({
                lesson_id: selectedLesson !== 'all' ? selectedLesson : '',
                title: '',
                description: '',
                instructions: '',
                materials_needed: '',
                passing_score: 70,
                max_attempts: 3,
                experiment_type: 'mcq',
                experiment_data: {}
            });
            setQuestions([]);
        }
        setShowModal(true);
    };

    // Utility to normalize question object shape
    const normalizeQuestion = (q) => ({
        question_text: q.question_text || '',
        question_type: q.question_type || 'mcq',
        options: Array.isArray(q.options) ? q.options.slice() : [],
        correct_answer: q.correct_answer ?? '',
        points: q.points ?? 1,
        id: q.id ?? undefined // optional if editing existing questions
    });

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingExperiment(null);
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }

        // If level select changed (from the modal), load terms/lessons accordingly — not used here
    };

    // Questions management
    const addQuestion = () => {
        setQuestions((prev) => [
            ...prev,
            {
                question_text: '',
                question_type: 'mcq',
                options: ['Option 1', 'Option 2'],
                correct_answer: '',
                points: 1
            }
        ]);
    };

    const removeQuestion = (idx) => {
        setQuestions((prev) => prev.filter((_, i) => i !== idx));
    };

    const updateQuestionField = (idx, field, value) => {
        setQuestions((prev) => {
            const copy = prev.slice();
            copy[idx] = { ...copy[idx], [field]: value };
            // If question type changed to non-mcq, clear options/correct_answer appropriately
            if (field === 'question_type' && value !== 'mcq') {
                copy[idx].options = [];
                if (value === 'true_false') {
                    copy[idx].options = ['True', 'False'];
                    // ensure correct_answer is 'True' or 'False'
                    if (copy[idx].correct_answer !== 'True' && copy[idx].correct_answer !== 'False') {
                        copy[idx].correct_answer = 'True';
                    }
                } else {
                    copy[idx].options = [];
                    copy[idx].correct_answer = '';
                }
            }
            return copy;
        });
    };

    const addOptionToQuestion = (qIdx) => {
        setQuestions((prev) => {
            const copy = prev.slice();
            copy[qIdx].options = copy[qIdx].options ? copy[qIdx].options.concat([`Option ${copy[qIdx].options.length + 1}`]) : ['Option 1'];
            return copy;
        });
    };

    const removeOptionFromQuestion = (qIdx, optIdx) => {
        setQuestions((prev) => {
            const copy = prev.slice();
            if (!copy[qIdx].options) return copy;
            copy[qIdx].options = copy[qIdx].options.filter((_, i) => i !== optIdx);
            // if removed option was the correct answer, clear it
            if (copy[qIdx].correct_answer && copy[qIdx].correct_answer === copy[qIdx].options[optIdx]) {
                copy[qIdx].correct_answer = '';
            }
            return copy;
        });
    };

    const updateOptionText = (qIdx, optIdx, text) => {
        setQuestions((prev) => {
            const copy = prev.slice();
            copy[qIdx].options = copy[qIdx].options.slice();
            copy[qIdx].options[optIdx] = text;
            return copy;
        });
    };

    // Validation
    const validate = () => {
        const newErrors = {};
        if (!formData.lesson_id) newErrors.lesson_id = 'Lesson is required';
        if (!formData.title || !formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.passing_score || formData.passing_score < 0) newErrors.passing_score = 'Valid passing score required';
        if (!formData.max_attempts || formData.max_attempts < 1) newErrors.max_attempts = 'Max attempts must be >= 1';

        // validate questions
        questions.forEach((q, idx) => {
            if (!q.question_text || !q.question_text.trim()) {
                newErrors[`question_${idx}_text`] = 'Question text required';
            }
            if (q.question_type === 'mcq') {
                if (!q.options || q.options.length < 2) {
                    newErrors[`question_${idx}_options`] = 'At least 2 options required for MCQ';
                } else {
                    // each option must have text
                    q.options.forEach((opt, oi) => {
                        if (!opt || !String(opt).trim()) {
                            newErrors[`question_${idx}_option_${oi}`] = 'Option text required';
                        }
                    });
                }
                if (!q.correct_answer) {
                    newErrors[`question_${idx}_correct`] = 'Correct answer required for MCQ';
                }
            }
            if (q.question_type === 'true_false') {
                if (q.correct_answer !== 'True' && q.correct_answer !== 'False') {
                    newErrors[`question_${idx}_correct`] = 'Correct answer must be True or False';
                }
            }
            if (!q.points || q.points < 0) {
                newErrors[`question_${idx}_points`] = 'Points must be >= 0';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        try {
            setLoading(true);

            const payload = {
                lesson_id: parseInt(formData.lesson_id, 10),
                title: formData.title,
                description: formData.description,
                instructions: formData.instructions,
                materials_needed: formData.materials_needed,
                passing_score: parseInt(formData.passing_score, 10),
                max_attempts: parseInt(formData.max_attempts, 10),
                experiment_type: formData.experiment_type,
                experiment_data: formData.experiment_data || {}
            };

            // include questions only if we have at least one
            if (Array.isArray(questions) && questions.length > 0) {
                // map to expected shape
                payload.questions = questions.map((q) => ({
                    question_text: q.question_text,
                    question_type: q.question_type,
                    options: Array.isArray(q.options) ? q.options : [],
                    correct_answer: q.correct_answer,
                    points: parseInt(q.points || 0, 10)
                }));
            }

            if (editingExperiment) {
                // update existing
                await adminService.updateExperiment(editingExperiment.id, payload);
                alert('Experiment updated successfully!');
            } else {
                await adminService.createExperiment(payload);
                alert('Experiment created successfully!');
            }

            handleCloseModal();
            fetchExperiments();
        } catch (err) {
            console.error('Failed to save experiment:', err);
            alert('Failed to save experiment: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
        try {
            setLoading(true);
            await adminService.deleteExperiment(id);
            alert('Experiment deleted successfully!');
            fetchExperiments();
        } catch (err) {
            console.error('Failed to delete experiment:', err);
            alert('Failed to delete experiment: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    // UI helpers
    const renderQuestionEditor = (q, idx) => {
        return (
            <div key={idx} className="border rounded-lg p-4 mb-3 bg-gray-50">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                        <label className="block text-sm font-medium mb-1">Question {idx + 1}</label>
                        <input
                            type="text"
                            value={q.question_text}
                            onChange={(e) => updateQuestionField(idx, 'question_text', e.target.value)}
                            className={`input-field ${errors[`question_${idx}_text`] ? 'border-red-500' : ''}`}
                            placeholder="Enter question text"
                        />
                        {errors[`question_${idx}_text`] && (
                            <p className="text-red-600 text-sm mt-1">{errors[`question_${idx}_text`]}</p>
                        )}
                    </div>

                    <div className="w-44">
                        <label className="block text-sm font-medium mb-1">Type</label>
                        <select
                            value={q.question_type}
                            onChange={(e) => updateQuestionField(idx, 'question_type', e.target.value)}
                            className="input-field"
                        >
                            {QUESTION_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* MCQ options editor */}
                {q.question_type === 'mcq' && (
                    <>
                        <div className="mb-2">
                            <label className="block text-sm font-medium mb-1">Options</label>
                            {q.options && q.options.length > 0 ? (
                                q.options.map((opt, oi) => (
                                    <div key={oi} className="flex items-center gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={opt}
                                            onChange={(e) => updateOptionText(idx, oi, e.target.value)}
                                            className={`input-field flex-1 ${errors[`question_${idx}_option_${oi}`] ? 'border-red-500' : ''}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeOptionFromQuestion(idx, oi)}
                                            className="p-2 rounded-md hover:bg-red-50 text-red-600"
                                        >
                                            <MinusCircle className="w-5 h-5" />
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500">No options yet</p>
                            )}
                            {errors[`question_${idx}_options`] && (
                                <p className="text-red-600 text-sm mt-1">{errors[`question_${idx}_options`]}</p>
                            )}
                            <div className="mt-1">
                                <button type="button" onClick={() => addOptionToQuestion(idx)} className="btn-primary inline-flex items-center gap-2">
                                    <PlusCircle className="w-4 h-4" /> Add Option
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 items-end">
                            <div>
                                <label className="block text-sm font-medium mb-1">Correct Answer</label>
                                <select
                                    value={q.correct_answer}
                                    onChange={(e) => updateQuestionField(idx, 'correct_answer', e.target.value)}
                                    className={`input-field ${errors[`question_${idx}_correct`] ? 'border-red-500' : ''}`}
                                >
                                    <option value="">Select correct answer</option>
                                    {q.options && q.options.map((opt, oi) => (
                                        <option key={oi} value={opt}>{opt}</option>
                                    ))}
                                </select>
                                {errors[`question_${idx}_correct`] && (
                                    <p className="text-red-600 text-sm mt-1">{errors[`question_${idx}_correct`]}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Points</label>
                                <input
                                    type="number"
                                    value={q.points}
                                    min="0"
                                    onChange={(e) => updateQuestionField(idx, 'points', parseInt(e.target.value || 0, 10))}
                                    className={`input-field ${errors[`question_${idx}_points`] ? 'border-red-500' : ''}`}
                                />
                                {errors[`question_${idx}_points`] && (
                                    <p className="text-red-600 text-sm mt-1">{errors[`question_${idx}_points`]}</p>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* True/False */}
                {q.question_type === 'true_false' && (
                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                            <label className="block text-sm font-medium mb-1">Correct Answer</label>
                            <select
                                value={q.correct_answer}
                                onChange={(e) => updateQuestionField(idx, 'correct_answer', e.target.value)}
                                className={`input-field ${errors[`question_${idx}_correct`] ? 'border-red-500' : ''}`}
                            >
                                <option value="">Select answer</option>
                                <option value="True">True</option>
                                <option value="False">False</option>
                            </select>
                            {errors[`question_${idx}_correct`] && (
                                <p className="text-red-600 text-sm mt-1">{errors[`question_${idx}_correct`]}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Points</label>
                            <input
                                type="number"
                                value={q.points}
                                min="0"
                                onChange={(e) => updateQuestionField(idx, 'points', parseInt(e.target.value || 0, 10))}
                                className={`input-field ${errors[`question_${idx}_points`] ? 'border-red-500' : ''}`}
                            />
                            {errors[`question_${idx}_points`] && (
                                <p className="text-red-600 text-sm mt-1">{errors[`question_${idx}_points`]}</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Text answer */}
                {q.question_type === 'text' && (
                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                            <label className="block text-sm font-medium mb-1">Sample/Model Answer (optional)</label>
                            <input
                                type="text"
                                value={q.correct_answer || ''}
                                onChange={(e) => updateQuestionField(idx, 'correct_answer', e.target.value)}
                                className="input-field"
                                placeholder="Optional model answer"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Points</label>
                            <input
                                type="number"
                                value={q.points}
                                min="0"
                                onChange={(e) => updateQuestionField(idx, 'points', parseInt(e.target.value || 0, 10))}
                                className={`input-field ${errors[`question_${idx}_points`] ? 'border-red-500' : ''}`}
                            />
                            {errors[`question_${idx}_points`] && (
                                <p className="text-red-600 text-sm mt-1">{errors[`question_${idx}_points`]}</p>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex justify-end mt-3">
                    <button type="button" onClick={() => removeQuestion(idx)} className="px-3 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 inline-flex items-center gap-2">
                        <Trash2 className="w-4 h-4" /> Remove Question
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Experiment Management</h2>
                    <p className="text-gray-600">Create and manage experiments (with questions)</p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Level Filter */}
                    <select
                        value={selectedLevel}
                        onChange={(e) => {
                            setSelectedLevel(e.target.value);
                            setSelectedTerm('all');
                            setSelectedLesson('all');
                            if (e.target.value !== 'all') fetchTermsByLevel(e.target.value);
                            fetchLessonsByTermOrLevel(e.target.value !== 'all' ? { level_id: e.target.value } : {});
                        }}
                        className="input-field"
                    >
                        <option value="all">All Levels</option>
                        {levels.map((l) => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                    </select>

                    {/* Term Filter */}
                    {selectedLevel !== 'all' && (
                        <select
                            value={selectedTerm}
                            onChange={(e) => {
                                setSelectedTerm(e.target.value);
                                setSelectedLesson('all');
                                if (e.target.value !== 'all') fetchLessonsByTermOrLevel({ term_id: e.target.value });
                            }}
                            className="input-field"
                        >
                            <option value="all">All Terms</option>
                            {terms.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    )}

                    {/* Lesson Filter */}
                    <select
                        value={selectedLesson}
                        onChange={(e) => setSelectedLesson(e.target.value)}
                        className="input-field"
                    >
                        <option value="all">All Lessons</option>
                        {lessons.map((ls) => (
                            <option key={ls.id} value={ls.id}>
                                {ls.title || ls.name}
                            </option>
                        ))}
                    </select>

                    <button onClick={() => handleOpenModal()} className="btn-primary flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Create Experiment
                    </button>
                </div>
            </div>

            {/* Experiments list */}
            {experiments.length === 0 ? (
                <div className="card text-center py-12">
                    <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No experiments found</p>
                    <button onClick={() => handleOpenModal()} className="btn-primary mt-4">Create First Experiment</button>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {experiments.map((exp) => (
                        <div key={exp.id} className="card hover:shadow-lg transition-all">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
                                    <Eye className="w-8 h-8 text-white" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <h3 className="font-bold text-lg line-clamp-2 mb-1">
                                                {exp.title}
                                            </h3>
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                                <span>{exp.lesson_title}</span>
                                                <span>•</span>
                                                <span>{exp.experiment_type}</span>
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <button onClick={() => handleOpenModal(exp)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium">
                                                <Edit className="w-4 h-4" /> Edit
                                            </button>
                                            <button onClick={() => handleDelete(exp.id, exp.title)} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium">
                                                <Trash2 className="w-4 h-4" /> Delete
                                            </button>
                                        </div>
                                    </div>

                                    {exp.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{exp.description}</p>}

                                    <div className="flex flex-wrap items-center gap-3 text-sm mb-3">
                                        {exp.instructions && (
                                            <span className="flex items-center gap-1 text-gray-600">
                        <FileText className="w-4 h-4" /> Instructions
                      </span>
                                        )}
                                        {exp.materials_needed && (
                                            <span className="flex items-center gap-1 text-gray-600">
                        <Clock className="w-4 h-4" /> Materials
                      </span>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8">
                        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between rounded-t-2xl">
                            <h2 className="text-2xl font-bold">{editingExperiment ? 'Edit Experiment' : 'Create Experiment'}</h2>
                            <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Lesson <span className="text-red-500">*</span></label>
                                    <select
                                        name="lesson_id"
                                        value={formData.lesson_id}
                                        onChange={handleChange}
                                        className={`input-field ${errors.lesson_id ? 'border-red-500' : ''}`}
                                    >
                                        <option value="">Select a lesson</option>
                                        {lessons.map((l) => (
                                            <option key={l.id} value={l.id}>{l.title || l.name}</option>
                                        ))}
                                    </select>
                                    {errors.lesson_id && <p className="text-red-600 text-sm mt-1">{errors.lesson_id}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Experiment Type</label>
                                    <select name="experiment_type" value={formData.experiment_type} onChange={handleChange} className="input-field">
                                        <option value="quiz">Quiz</option>
                                        <option value="interactive">Interactive</option>
                                        <option value="simulation">Simulation</option>
                                        <option value="practical">Practical</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Title <span className="text-red-500">*</span></label>
                                <input type="text" name="title" value={formData.title} onChange={handleChange} className={`input-field ${errors.title ? 'border-red-500' : ''}`} placeholder="Enter experiment title" />
                                {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Description</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} rows="3" className="input-field" placeholder="Short description..." />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Instructions</label>
                                <textarea name="instructions" value={formData.instructions} onChange={handleChange} rows="3" className="input-field" placeholder="Step-by-step instructions..." />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Materials Needed</label>
                                <input type="text" name="materials_needed" value={formData.materials_needed} onChange={handleChange} className="input-field" placeholder="e.g., magnet, paper clips..." />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Passing Score</label>
                                    <input type="number" name="passing_score" value={formData.passing_score} min="0" onChange={handleChange} className={`input-field ${errors.passing_score ? 'border-red-500' : ''}`} />
                                    {errors.passing_score && <p className="text-red-600 text-sm mt-1">{errors.passing_score}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Max Attempts</label>
                                    <input type="number" name="max_attempts" value={formData.max_attempts} min="1" onChange={handleChange} className={`input-field ${errors.max_attempts ? 'border-red-500' : ''}`} />
                                    {errors.max_attempts && <p className="text-red-600 text-sm mt-1">{errors.max_attempts}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Experiment Data (JSON)</label>
                                    <input type="text" name="experiment_data" value={JSON.stringify(formData.experiment_data || {})} onChange={(e) => {
                                        let val = e.target.value;
                                        try {
                                            // try to parse JSON; if invalid, keep string in formData.experiment_data as raw string
                                            const parsed = JSON.parse(val);
                                            setFormData(prev => ({ ...prev, experiment_data: parsed }));
                                            if (errors.experiment_data) setErrors(prev => ({ ...prev, experiment_data: '' }));
                                        } catch (err) {
                                            // store as string for now
                                            setFormData(prev => ({ ...prev, experiment_data: val }));
                                        }
                                    }} className="input-field" placeholder='{"foo":"bar"}' />
                                </div>
                            </div>

                            {/* Questions builder */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium">Questions</label>
                                    <div>
                                        <button type="button" onClick={addQuestion} className="btn-primary inline-flex items-center gap-2">
                                            <Plus className="w-4 h-4" /> Add Question
                                        </button>
                                    </div>
                                </div>

                                {questions.length === 0 ? (
                                    <div className="p-4 border rounded-lg text-sm text-gray-500">
                                        No questions added. Add questions to make this experiment interactive.
                                    </div>
                                ) : (
                                    questions.map((q, idx) => renderQuestionEditor(q, idx))
                                )}
                            </div>

                            <div className="flex gap-3 pt-4 sticky bottom-0 bg-white border-t -mx-6 px-6 py-4">
                                <button type="button" onClick={handleCloseModal} className="flex-1 btn-secondary">Cancel</button>
                                <button type="submit" disabled={loading} className="flex-1 btn-primary flex items-center justify-center gap-2">
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            {editingExperiment ? 'Update Experiment' : 'Create Experiment'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExperimentManagement;
